import i18n from '@/utils/i18n';
import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { SCREEN_PADDING } from '@/styles';
import { Toast, ToastOptions, Type } from '@/components/toast';
import { ToastKavErrorBoundary } from '@/components/toast-kav-error-boundary';
import { v4 as uuidv4 } from 'uuid';

interface Toast {
  options: ToastOptions;
  uuid: string;
}

export interface ToastContextType {
  closeToast: (uuid: string) => void;
  showErrorToast: (message?: string, onClose?: () => void) => string | void;
  showToast: (options: ToastOptions) => string | void;
}

export const ToastContext = createContext<ToastContextType>({
  closeToast: () => {},
  showErrorToast: () => {},
  showToast: () => {},
});

export const useToasts = () => useContext(ToastContext);

export enum Position {
  BOTTOM = 'bottom',
  TOP = 'top',
}

const DEFAULT_POSITION = Platform.OS === 'web' ? Position.TOP : Position.BOTTOM;

interface Props {
  children?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  position?: Position;
}

const MAX_TOASTS = 2;

export const ToastContextProvider: React.FC<Props> = ({
  children,
  containerStyle,
  position = DEFAULT_POSITION,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const value = useMemo<ToastContextType>(() => {
    const showToast = (options: ToastOptions) => {
      const toast = { options, uuid: uuidv4() };
      setToasts((previousToasts) =>
        [...previousToasts, toast].slice(-MAX_TOASTS),
      );
      return toast.uuid;
    };

    return {
      closeToast: (uuid: string) => {
        setToasts((previousToasts) =>
          previousToasts.filter((t) => t.uuid !== uuid),
        );
      },
      showErrorToast: (message?: string, onClose?: () => void) => {
        return showToast({
          content: message || i18n.t('toast.standard_error'),
          onClose,
          type: Type.NEGATIVE,
        });
      },
      showToast,
    };
  }, []);

  const toastComponents = toasts.map(({ options, uuid }) => (
    <Toast
      key={uuid}
      onClose={() => value.closeToast(uuid)}
      options={options}
    />
  ));

  if (position === Position.TOP) {
    // If toasts are rendering at the top of the screen, reverse the order
    // so most recent is at the top.
    toastComponents.reverse();
  }

  const containerStyleArray = [
    styles.container,
    position === Position.BOTTOM ? styles.containerBottom : styles.containerTop,
    containerStyle,
  ];

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastKavErrorBoundary
        fallback={
          <View pointerEvents="box-none" style={containerStyleArray}>
            {toastComponents}
          </View>
        }
      >
        <KeyboardAvoidingView
          behavior={Platform.select({ android: undefined, ios: 'position' })}
          keyboardVerticalOffset={24}
          // Set box-none so the container doesn't block interaction with elements
          // underneath it but allows interaction with the toasts themselves
          pointerEvents="box-none"
          style={containerStyleArray}
        >
          {toastComponents}
        </KeyboardAvoidingView>
      </ToastKavErrorBoundary>
    </ToastContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: {
    left: 0,
    paddingHorizontal: SCREEN_PADDING,
    position: 'absolute',
    right: 0,
  },
  containerBottom: {
    bottom: SCREEN_PADDING,
  },
  containerTop: {
    top: SCREEN_PADDING,
  },
});
